import { useContext, useRef, useState } from 'react'
import { useStoreActions } from 'easy-peasy'
import { client } from '../../client'
import useEffectAsync from '../../hooks/useEffectAsync'
import { useOnline } from '../../hooks/useOnline'
import { useNotificationBar } from '../../components/NotificationBar'
import { useTranslation } from 'react-i18next'
import { MenuContext } from './index'
import AuthenticationService from '../../services/AuthenticationService'
import { getYear } from 'date-fns/getYear'
import { Funnel, TreatmentProgram } from '../../types'

function stripErrorFields(
  values: any,
  errors: any
): { values: any; didStrip: boolean } {
  if (!errors || !values || typeof errors !== 'object' || typeof values !== 'object') {
    return { values, didStrip: false }
  }

  let didStrip = false
  const result = { ...values }

  for (const key of Object.keys(errors)) {
    if (!(key in result)) continue

    if (typeof errors[key] === 'string') {
      delete result[key]
      didStrip = true
    } else if (typeof errors[key] === 'object' && errors[key] !== null) {
      const nested = stripErrorFields(result[key], errors[key])
      if (nested.didStrip) {
        didStrip = true
        if (
          nested.values &&
          typeof nested.values === 'object' &&
          Object.keys(nested.values).length > 0
        ) {
          result[key] = nested.values
        } else {
          delete result[key]
        }
      }
    }
  }

  return { values: result, didStrip }
}

function stripInvalidDateActivities(
  activities: Record<string, any>
): { cleaned: Record<string, any>; didStrip: boolean } {
  const cleaned: Record<string, any> = {}
  let didStrip = false

  for (const [key, activity] of Object.entries(activities)) {
    if (activity?.date && isNaN(new Date(activity.date).getTime())) {
      didStrip = true
      continue
    }
    cleaned[key] = activity
  }

  return { cleaned, didStrip }
}

export function useRecordPageContent(
  patientId: string | undefined,
  treatmentProgramId: string | undefined
) {
  const { t } = useTranslation('recordPage')

  const { setUpdatedJustNow } = useStoreActions((actions) => ({
    // @ts-ignore
    setUpdatedJustNow: actions.user.setUpdatedJustNow
  }))
  const { setIsZPM } = useStoreActions((actions) => ({
    // @ts-ignore
    setIsZPM: actions.user.setIsZPM
  }))
  const [loading, setLoading] = useState(true)
  const [treatmentProgramAndFunnel, setTreatmentProgramAndFunnel] =
    useState<TreatmentProgram>()
  const [funnel, setFunnel] = useState<Funnel>()
  const [otherPatientFunnels, setOtherPatientFunnels] = useState<Funnel[]>()
  const { showMessage, NotificationBar } = useNotificationBar(undefined)
  const [intakeWarning, setIntakeWarning] = useState<string | null>(null)
  const isOnline = useOnline()
  const { hideMenuItem } = useContext(MenuContext)
  const canRemoveRecord =
    AuthenticationService.isBackOffice() ||
    AuthenticationService.isAdministrator() ||
    AuthenticationService.isSuperUser()

  // Zorgprestatiemodel (ZPM) is the health care system from 2022 onwards.
  const isZPM =
    !treatmentProgramAndFunnel?.startDate ||
    getYear(new Date(treatmentProgramAndFunnel?.startDate)) >= 2022

  setIsZPM(isZPM)

  const validationWarningShownRef = useRef(false)

  const save = async (values: any, errors: any) => {
    if (!isOnline) {
      showMessage(t('offlineMessage'), 'error')
      return
    }
    if (!treatmentProgramAndFunnel || !treatmentProgramId) {
      showMessage(t('noResponseMessage'), 'error')
      return
    }

    let hasSkippedFields = false

    // Strip fields with validation errors instead of skipping the entire save
    if (errors && typeof errors === 'object') {
      const stripped = stripErrorFields(values, errors)
      if (stripped.didStrip) {
        hasSkippedFields = true
        values = stripped.values
      }
    }

    // Strip individual activities with invalid dates instead of skipping the entire save
    if (values.activities2022) {
      const { cleaned, didStrip } = stripInvalidDateActivities(values.activities2022)
      if (didStrip) {
        hasSkippedFields = true
        values.activities2022 = Object.keys(cleaned).length > 0 ? cleaned : undefined
      }
    }
    if (values.activities) {
      const { cleaned, didStrip } = stripInvalidDateActivities(values.activities)
      if (didStrip) {
        hasSkippedFields = true
        values.activities = Object.keys(cleaned).length > 0 ? cleaned : undefined
      }
    }

    // Clean up undefined keys left by activity stripping
    if (values.activities2022 === undefined) delete values.activities2022
    if (values.activities === undefined) delete values.activities

    // Nothing left to save after stripping invalid fields
    if (!values || Object.keys(values).length === 0) {
      if (hasSkippedFields && !validationWarningShownRef.current) {
        validationWarningShownRef.current = true
        showMessage(t('validationSkipMessage'), 'error')
        setTimeout(() => { validationWarningShownRef.current = false }, 6000)
      }
      return
    }

    // Show a one-time warning that some fields were skipped
    if (hasSkippedFields && !validationWarningShownRef.current) {
      validationWarningShownRef.current = true
      showMessage(t('validationSkipMessage'), 'error')
      setTimeout(() => { validationWarningShownRef.current = false }, 6000)
    }

    const { funnel: funnelData, ...treatmentProgramData } = values

    // Save funnel data independently so a failure here doesn't block treatment program data
    if (funnelData) {
      try {
        let funnelIdToUpdate = treatmentProgramAndFunnel?.funnel?.id
        if (!funnelIdToUpdate) {
          const ensured = await client.ensureFunnelForProgram(
            parseInt(treatmentProgramId)
          )
          if (ensured?.status === 'success') {
            funnelIdToUpdate = ensured?.data?.id
          }
        }
        if (funnelIdToUpdate) {
          await client.updateFunnel(funnelIdToUpdate, funnelData)
        }
      } catch (e: any) {
        console.error('Failed to save funnel data:', e)
        showMessage(t('partialSaveMessage'), 'error')
      }
    }

    // Save treatment program data (patient, notes, activities, etc.)
    if (Object.keys(treatmentProgramData).length > 0) {
      try {
        // Remove UI-only fields that should not be persisted to the backend
        const { referrerDetailsValid, ...safeTreatmentProgramData } =
          (treatmentProgramData as any) || {}

        // Ensure any UI-only nested fields are stripped as well
        if (safeTreatmentProgramData?.patient) {
          const { bsnDuplicateError, ...safePatient } =
            safeTreatmentProgramData.patient || {}
          ;(safeTreatmentProgramData as any).patient = safePatient
        }

        const result = await client.saveTreatmentProgram(
          parseInt(treatmentProgramId),
          safeTreatmentProgramData
        )

        if (result?.status === 'partial') {
          showMessage(t('partialSaveMessage'), 'error')
        }
      } catch (e: any) {
        if (!e.response) {
          showMessage(t('noResponseMessage'), 'error')
          return
        }

        switch (e.response.status) {
          case 409:
            showMessage(t('error409Message'), 'error')
            break
          default:
            showMessage(t('errorMessage'), 'error')
            break
        }
        return
      }
    }

    setUpdatedJustNow(true)
    setTimeout(() => setUpdatedJustNow(false), 5000)
  }

  const handleRemovePatient = async (reason: any) => {
    if (
      !treatmentProgramAndFunnel ||
      !funnel ||
      !treatmentProgramAndFunnel.patient
    ) {
      showMessage(t('errorMessage'), 'error')
      return
    }
    await client.removeFunnel(funnel.id)
    await client.removePatient({
      id: treatmentProgramAndFunnel.patient.id,
      reason
    })
    window.location.href = '/'
  }

  useEffectAsync(async () => {
    if (!treatmentProgramId || !patientId) return
    
    setLoading(true)
    let assignedFunnel
    let otherFetchedPatientFunnels
    try {
      const treatment = await client.getTreatmentProgramById(treatmentProgramId)

      if (treatment?.patient) {
        if (treatment.patient.bsn && treatment.patient.bsn !== treatment.patient.bsn.trim()) {
          const trimmedBsn = treatment.patient.bsn.trim() || (null as any)
          treatment.patient.bsn = trimmedBsn
          client.updatePatient(treatment.patient.id, { bsn: trimmedBsn }).catch(() => {
            // silently ignore — the in-memory fix still works for this session
          })
        }
      }

      // Both funnel lookups only need `treatment`, not each other, so fetch
      // them together instead of chaining. On a large file this removes a
      // whole round-trip from the time the loading spinner is on screen.
      if (treatment) {
        ;[otherFetchedPatientFunnels, assignedFunnel] = await Promise.all([
          treatment.patient
            ? client.getFunnelsByPatientId(treatment.patient.id)
            : Promise.resolve(undefined),
          client.findFunnelByTreatmentProgramId(treatmentProgramId)
        ])

        // Determine a sensible default language for newly created/legacy trajects
        const previousLanguage =
          otherFetchedPatientFunnels?.find((f: any) => !!f?.language)?.language ||
          'nl'

        if (assignedFunnel) {
          if (!assignedFunnel.language) {
            assignedFunnel.language = previousLanguage
          }
          treatment.funnel = assignedFunnel
        } else {
          // No funnel yet — prefill language so the radio shows a selection;
          // actual funnel will be created only if the user changes/saves funnel fields
          ;(treatment as any).funnel = { language: previousLanguage }
        }
        setFunnel(assignedFunnel)
      }
      setTreatmentProgramAndFunnel(treatment)
      setOtherPatientFunnels(otherFetchedPatientFunnels)

      // Warning: intake >= 11 months ago and no end date.
      // The activities already came back on the treatment program above, so
      // this used to re-request the exact same rows (~120 kB on a large file)
      // and awaited them before the spinner could clear.
      try {
        const activities = treatment?.activities2022
        const diagnostic = activities?.find((a: any) => a?.consultationType?.name?.toLowerCase() === 'diagnostiek')
        if (diagnostic && !treatment?.endDate) {
          const elevenMonthsMs = 11 * 30 * 24 * 60 * 60 * 1000 // approximate 11 months
          const intakeMs = new Date(diagnostic.date).getTime()
          if (!Number.isNaN(intakeMs) && Date.now() - intakeMs >= elevenMonthsMs) {
            setIntakeWarning(t('intakeOlderThan11MonthsWarning'))
          }
        }
      } catch (e) {
        // ignore warning fetch issues
      }
    } catch (e) {
      // No funnel for this client.
      hideMenuItem('contactHistory')
    }
    setLoading(false)
  }, [patientId, treatmentProgramId])

  const allStatusLogs = otherPatientFunnels
    ?.map((funnel) =>
      funnel.statusLog.map((logItem) => ({
        treatmentProgramId: funnel.treatmentProgram?.id,
        ...logItem
      }))
    )
    .reduce((a, b) => [...a, ...b], [])
    .sort(
      (prev, curr) =>
        new Date(prev.createdAt).getTime() - new Date(curr.createdAt).getTime()
    )

  return {
    treatmentProgramAndFunnel,
    loading,
    save,
    canRemoveRecord,
    handleRemovePatient,
    mainFunnel: funnel,
    isZPM,
    NotificationBar,
    allStatusLogs,
    otherPatientFunnels,
    intakeWarning
  }
}
